interface DeveloperOptions {
  unlockAllContent: boolean;
  infiniteCurrency: boolean;
  godMode: boolean;
  speedMultiplier: number;
  skipWaves: boolean;
  debugMode: boolean;
}

class DeveloperOptionsManager {
  private static instance: DeveloperOptionsManager;
  private options: DeveloperOptions;
  private isEnabled: boolean = false;

  private constructor() {
    this.options = {
      unlockAllContent: false,
      infiniteCurrency: false,
      godMode: false,
      speedMultiplier: 1,
      skipWaves: false,
      debugMode: false
    };
    
    this.loadFromStorage();
  }

  static getInstance(): DeveloperOptionsManager {
    if (!DeveloperOptionsManager.instance) {
      DeveloperOptionsManager.instance = new DeveloperOptionsManager();
    }
    return DeveloperOptionsManager.instance;
  }

  enableDeveloperMode(secretCode: string): boolean {
    const validCodes = ['EARTHDEV', 'UNLOCK123', 'DEVMODE'];
    if (validCodes.includes(secretCode.toUpperCase())) {
      this.isEnabled = true;
      this.saveToStorage();
      console.log('🔓 Developer Mode Enabled!');
      return true;
    }
    return false;
  }

  isDeveloperModeEnabled(): boolean {
    return this.isEnabled;
  }

  setOption(key: keyof DeveloperOptions, value: any): void {
    if (!this.isEnabled) return;
    
    this.options[key] = value as never;
    this.saveToStorage();
    console.log(`🔧 Dev Option: ${key} = ${value}`);
    
    // Apply immediate effects
    this.applyOptionEffects(key, value);
  }

  getOption(key: keyof DeveloperOptions): any {
    return this.options[key];
  }

  getAllOptions(): DeveloperOptions {
    return { ...this.options };
  }

  unlockAllContent(): void {
    if (!this.isEnabled) return;
    
    const userProfile = (window as any).userProfile;
    if (!userProfile) return;

    // Unlock all Earths
    userProfile.ownedEarths = ['classic', 'ice', 'volcanic', 'crystal', 'quantum', 'neutron'];
    
    // Unlock all Towers
    userProfile.ownedTowers = ['laser', 'rapid', 'heavy', 'missile', 'freeze', 'multi', 'piercing', 'chain', 'shotgun', 'beam'];
    
    // Unlock all Pets
    userProfile.ownedPets = ['laser', 'missile', 'drone', 'shield', 'support'];
    
    // Set high level to unlock everything
    userProfile.highestLevel = 100;
    
    // Give lots of currency
    if (this.options.infiniteCurrency) {
      userProfile.gold = 999999;
      userProfile.ruby = 99999;
    } else {
      userProfile.gold = Math.max(userProfile.gold, 50000);
      userProfile.ruby = Math.max(userProfile.ruby, 5000);
    }
    
    // Save profile
    localStorage.setItem('earthDefenseProfile', JSON.stringify(userProfile));
    (window as any).userProfile = userProfile;
    
    console.log('🎮 All content unlocked!');
  }

  private applyOptionEffects(key: keyof DeveloperOptions, value: any): void {
    switch (key) {
      case 'unlockAllContent':
        if (value) this.unlockAllContent();
        break;
        
      case 'infiniteCurrency':
        if (value) {
          const userProfile = (window as any).userProfile;
          if (userProfile) {
            userProfile.gold = 999999;
            userProfile.ruby = 99999;
            localStorage.setItem('earthDefenseProfile', JSON.stringify(userProfile));
          }
        }
        break;
        
      case 'debugMode':
        if (value) {
          console.log('🐛 Debug mode enabled - check console for detailed logs');
        }
        break;
    }
  }

  createDevPanel(scene: Phaser.Scene): void {
    if (!this.isEnabled) return;
    
    const width = scene.cameras.main.width;
    const height = scene.cameras.main.height;
    
    // Dev panel background
    const devPanel = scene.add.graphics();
    devPanel.fillStyle(0x000000, 0.8);
    devPanel.fillRoundedRect(width - 250, 10, 240, 300, 10);
    devPanel.lineStyle(2, 0xff6b6b, 1);
    devPanel.strokeRoundedRect(width - 250, 10, 240, 300, 10);
    devPanel.setDepth(5000);
    devPanel.setVisible(false);
    
    // Dev panel title
    const devTitle = scene.add.text(width - 130, 25, 'DEV OPTIONS', {
      fontSize: '14px',
      fontFamily: 'Arial Black',
      color: '#ff6b6b',
      fontStyle: 'bold'
    }).setOrigin(0.5).setDepth(5001);
    devTitle.setVisible(false);
    
    // Toggle button
    const toggleBtn = scene.add.text(width - 30, 30, '🔧', {
      fontSize: '20px',
      fontFamily: 'Arial'
    }).setOrigin(0.5).setDepth(5002);
    
    toggleBtn.setInteractive({ useHandCursor: true });
    
    let panelVisible = false;
    toggleBtn.on('pointerdown', () => {
      panelVisible = !panelVisible;
      devPanel.setVisible(panelVisible);
      devTitle.setVisible(panelVisible);
      
      if (panelVisible) {
        this.createDevButtons(scene, width, devPanel.depth);
      }
    });
    
    // Store references for cleanup
    (scene as any).devPanel = devPanel;
    (scene as any).devTitle = devTitle;
    (scene as any).devToggle = toggleBtn;
  }

  private createDevButtons(scene: Phaser.Scene, screenWidth: number, depth: number): void {
    const buttons = [
      { text: 'Unlock All', action: () => this.setOption('unlockAllContent', true) },
      { text: 'Infinite $$$', action: () => this.setOption('infiniteCurrency', true) },
      { text: 'God Mode', action: () => this.setOption('godMode', !this.options.godMode) },
      { text: 'Debug Mode', action: () => this.setOption('debugMode', !this.options.debugMode) },
      { text: 'Speed x2', action: () => this.setOption('speedMultiplier', this.options.speedMultiplier === 1 ? 2 : 1) }
    ];
    
    buttons.forEach((button, index) => {
      const y = 60 + (index * 40);
      
      const btnBg = scene.add.graphics();
      btnBg.fillStyle(0x2c3e50, 0.9);
      btnBg.fillRoundedRect(screenWidth - 230, y - 15, 180, 30, 5);
      btnBg.lineStyle(1, 0x74b9ff, 0.8);
      btnBg.strokeRoundedRect(screenWidth - 230, y - 15, 180, 30, 5);
      btnBg.setDepth(depth + 1);
      
      const btnText = scene.add.text(screenWidth - 140, y, button.text, {
        fontSize: '12px',
        fontFamily: 'Arial',
        color: '#ffffff',
        fontStyle: 'bold'
      }).setOrigin(0.5).setDepth(depth + 2);
      
      btnBg.setInteractive(
        new Phaser.Geom.Rectangle(screenWidth - 230, y - 15, 180, 30),
        Phaser.Geom.Rectangle.Contains
      );
      
      btnBg.on('pointerdown', button.action);
      btnBg.on('pointerover', () => {
        btnBg.clear();
        btnBg.fillStyle(0x34495e, 0.9);
        btnBg.fillRoundedRect(screenWidth - 230, y - 15, 180, 30, 5);
        btnBg.lineStyle(1, 0x74b9ff, 1);
        btnBg.strokeRoundedRect(screenWidth - 230, y - 15, 180, 30, 5);
      });
      
      btnBg.on('pointerout', () => {
        btnBg.clear();
        btnBg.fillStyle(0x2c3e50, 0.9);
        btnBg.fillRoundedRect(screenWidth - 230, y - 15, 180, 30, 5);
        btnBg.lineStyle(1, 0x74b9ff, 0.8);
        btnBg.strokeRoundedRect(screenWidth - 230, y - 15, 180, 30, 5);
      });
    });
  }

  private loadFromStorage(): void {
    try {
      const saved = localStorage.getItem('earthDefenseDeveloperOptions');
      if (saved) {
        const data = JSON.parse(saved);
        this.isEnabled = data.isEnabled || false;
        this.options = { ...this.options, ...data.options };
      }
    } catch (error) {
      console.warn('Could not load developer options from storage');
    }
  }

  private saveToStorage(): void {
    try {
      const data = {
        isEnabled: this.isEnabled,
        options: this.options
      };
      localStorage.setItem('earthDefenseDeveloperOptions', JSON.stringify(data));
    } catch (error) {
      console.warn('Could not save developer options to storage');
    }
  }

  // Secret key combination detector
  static setupSecretKeyDetector(scene: Phaser.Scene): void {
    const devManager = DeveloperOptionsManager.getInstance();
    let keySequence: string[] = [];
    const secretSequence = ['D', 'E', 'V', 'M', 'O', 'D', 'E']; // DEVMODE
    
    scene.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      keySequence.push(event.code.replace('Key', ''));
      console.log('keySequence', keySequence);
      if (keySequence.length > secretSequence.length) {
        keySequence.shift();
      }
      
      if (keySequence.join('') === secretSequence.join('')) {
        if (devManager.enableDeveloperMode('DEVMODE')) {
          // Show activation message
          const message = scene.add.text(
            scene.cameras.main.centerX,
            scene.cameras.main.centerY,
            '🔓 DEVELOPER MODE ACTIVATED! 🔓',
            {
              fontSize: '24px',
              fontFamily: 'Arial Black',
              color: '#ff6b6b',
              fontStyle: 'bold'
            }
          ).setOrigin(0.5).setDepth(9999);
          
          scene.tweens.add({
            targets: message,
            alpha: 0,
            y: message.y - 50,
            duration: 3000,
            ease: 'Power2.easeOut',
            onComplete: () => message.destroy()
          });
          
          devManager.createDevPanel(scene);
        }
        keySequence = [];
      }
    });
  }
}

export { DeveloperOptionsManager, DeveloperOptions }; 